import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaintBrushIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface AppearanceSettings {
  theme: 'light' | 'dark' | 'auto';
  accentColor: string;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  compactMode: boolean;
}

const AppearanceSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState<AppearanceSettings>({
    theme: 'light',
    accentColor: '#4F46E5',
    fontSize: 'medium',
    reducedMotion: false,
    compactMode: false,
  });

  const accentColors = [
    { name: 'Indigo', value: '#4F46E5' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#9333EA' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Green', value: '#10B981' },
    { name: 'Teal', value: '#14B8A6' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/appearance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.settings) {
          setSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/appearance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ settings }),
      });

      if (res.ok) {
        setSuccess(true);
        // Apply theme immediately
        applyTheme(settings.theme);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (theme: 'light' | 'dark' | 'auto') => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else {
      // Auto mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Appearance & Theme</h1>
        <p className="mt-2 text-sm text-gray-600">
          Customize how the platform looks and feels
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Theme */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <PaintBrushIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Theme</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setSettings({ ...settings, theme: 'light' })}
              className={`p-4 border-2 rounded-lg text-center ${
                settings.theme === 'light'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-4xl mb-2">☀️</div>
              <div className="text-sm font-medium">Light</div>
            </button>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, theme: 'dark' })}
              className={`p-4 border-2 rounded-lg text-center ${
                settings.theme === 'dark'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-4xl mb-2">🌙</div>
              <div className="text-sm font-medium">Dark</div>
            </button>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, theme: 'auto' })}
              className={`p-4 border-2 rounded-lg text-center ${
                settings.theme === 'auto'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-4xl mb-2">🌓</div>
              <div className="text-sm font-medium">Auto</div>
            </button>
          </div>
        </div>

        {/* Accent Color */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Accent Color</h2>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
            {accentColors.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => setSettings({ ...settings, accentColor: color.value })}
                className={`relative w-12 h-12 rounded-lg border-2 ${
                  settings.accentColor === color.value
                    ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                    : 'border-gray-200'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              >
                {settings.accentColor === color.value && (
                  <CheckCircleIcon className="absolute inset-0 m-auto w-6 h-6 text-white" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Font Size */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Font Size</h2>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.fontSize === 'small'}
                onChange={() => setSettings({ ...settings, fontSize: 'small' })}
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3 text-sm">Small</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.fontSize === 'medium'}
                onChange={() => setSettings({ ...settings, fontSize: 'medium' })}
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3 text-base">Medium (Recommended)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={settings.fontSize === 'large'}
                onChange={() => setSettings({ ...settings, fontSize: 'large' })}
                className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span className="ml-3 text-lg">Large</span>
            </label>
          </div>
        </div>

        {/* Display Options */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Display Options</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Reduced Motion</span>
                <p className="text-xs text-gray-500 mt-1">Minimize animations and transitions</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setSettings({ ...settings, reducedMotion: !settings.reducedMotion })
                }
                className={`${
                  settings.reducedMotion ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.reducedMotion ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Compact Mode</span>
                <p className="text-xs text-gray-500 mt-1">Show more content on screen</p>
              </div>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, compactMode: !settings.compactMode })}
                className={`${
                  settings.compactMode ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    settings.compactMode ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Appearance'}
          </button>
        </div>

        {success && (
          <div className="flex items-center justify-center p-4 bg-green-50 rounded-md">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-800">
              Appearance settings saved successfully!
            </span>
          </div>
        )}
      </form>
    </div>
  );
};

export default AppearanceSettingsPage;
